/**
 * Bottin de RÉINSCRIPTION — document imprimable A4 paysage.
 * Un tableau par OPTION, avec les moyennes de chaque semestre (L1 : S1-S2,
 * L2 : S1-S4) + moyenne générale + éligibilité. En-tête 2i + signature du
 * chef de la scolarité. 2026-10-15.
 */

export interface BottinReinscEtudiant {
  matricule: string;
  nom: string;
  prenoms: string;
  sexe: string;
  moyenne_generale: number | null;
  nb_semestres: number;
  complet: boolean;
  eligible: boolean;
  moyenne_s1?: number | null;
  moyenne_s2?: number | null;
  moyenne_s3?: number | null;
  moyenne_s4?: number | null;
}
export interface BottinReinscGroupe {
  option: string;
  pole?: string;
  effectif: number;
  eligibles: number;
  etudiants: BottinReinscEtudiant[];
}
export interface DonneesBottinReinscription {
  niveau: 'L1' | 'L2';
  annee_academique: string;
  campus: string;
  responsable_academique: string;
  semestres: number[];
  seuil_eligibilite: number;
  effectif_total: number;
  eligibles_total: number;
  groupes: BottinReinscGroupe[];
}
export interface BottinReinscOptions {
  ville?: string;
  chefScolarite?: string;
  dateEmission?: string;
}

const esc = (s: any) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const fmt = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toFixed(2).replace('.', ',');
const fmtSexe = (s: string) => {
  const u = (s || '').toUpperCase();
  if (u.startsWith('F')) return 'F';
  if (u.startsWith('M') || u.startsWith('H')) return 'M';
  return '';
};
const fmtDate = (d?: string) => {
  const dt = d ? new Date(d) : new Date();
  if (isNaN(dt.getTime())) return d || '';
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

export function buildBottinReinscriptionHTML(
  data: DonneesBottinReinscription,
  opts: BottinReinscOptions = {},
): string {
  const ville = opts.ville || data.campus || 'Pointe-Noire';
  const chef = opts.chefScolarite || data.responsable_academique || '';
  const anneeAff = data.annee_academique.replace('-', ' – ');
  const sems = data.semestres && data.semestres.length ? data.semestres : [1, 2];
  const niveauLong = data.niveau === 'L1' ? 'Première année (L1)' : 'Deuxième année (L2)';

  const semCols = sems.map((s) => `<th class="moy">S${s}</th>`).join('');
  const semColspan = sems.length;

  // Tableaux par option
  let blocs = '';
  data.groupes.forEach((g) => {
    let rows = '';
    g.etudiants.forEach((e, i) => {
      const semCells = sems
        .map((s) => {
          const v = (e as any)[`moyenne_s${s}`] as number | null | undefined;
          const cls = v !== null && v !== undefined && v < 10 ? 'moy sous' : 'moy';
          return `<td class="c ${cls}">${fmt(v)}</td>`;
        })
        .join('');
      rows += `<tr>
        <td class="c">${i + 1}</td>
        <td class="c">${esc(e.matricule)}</td>
        <td class="l"><strong>${esc(e.nom)}</strong> ${esc(e.prenoms)}</td>
        <td class="c">${fmtSexe(e.sexe)}</td>
        ${semCells}
        <td class="c moy gen">${fmt(e.moyenne_generale)}</td>
        <td class="c ${e.eligible ? 'ok' : 'ko'}">${e.eligible ? 'OUI' : 'NON'}</td>
      </tr>`;
    });
    blocs += `
    <div class="bloc">
      <div class="opt-title">${esc(g.option)} <span class="opt-meta">— ${g.effectif} étudiant(s), ${g.eligibles} éligible(s)</span></div>
      <table class="grille">
        <thead><tr>
          <th style="width:28px">N°</th>
          <th style="width:80px">Matricule</th>
          <th>Nom &amp; Prénoms</th>
          <th style="width:28px">Sexe</th>
          ${semCols}
          <th class="moy">Moy. Gén.</th>
          <th style="width:58px">Éligible</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  });

  return `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">
<title>Bottin de réinscription ${data.niveau} — ${anneeAff}</title>
<style>
  @page { size: A4 landscape; margin: 9mm 11mm; }
  @media print { .no-print { display:none !important; } }
  body { font-family:'Calibri',Arial,sans-serif; font-size:10.5px; color:#000; margin:0; line-height:1.3; }
  .entete { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #1e3a5f; padding-bottom:6px; margin-bottom:8px; }
  .entete .col { font-size:9px; line-height:1.3; }
  .entete .col.g { text-align:left; max-width:40%; }
  .entete .col.d { text-align:right; max-width:30%; }
  .entete .mid { text-align:center; flex:1; }
  .entete .mid img { height:54px; }
  .entete .inst { font-weight:bold; color:#1e3a5f; font-size:10px; }
  .doc-title { text-align:center; font-size:16px; font-weight:bold; letter-spacing:0.4px; color:#1e3a5f; margin:6px 0 2px; }
  .doc-sub { text-align:center; font-size:11px; margin-bottom:8px; }
  .meta { display:flex; justify-content:space-between; font-size:10.5px; margin:6px 2px; }
  .meta b { color:#1e3a5f; }
  .bloc { margin:8px 0; page-break-inside:auto; }
  .opt-title { background:#1e3a5f; color:#fff; font-weight:bold; font-size:11px; padding:4px 8px; }
  .opt-title .opt-meta { font-weight:normal; font-size:9.5px; opacity:.9; }
  table.grille { border-collapse:collapse; width:100%; font-size:9.5px; }
  table.grille th, table.grille td { border:1px solid #333; padding:3px 4px; }
  table.grille th { background:#eef2f7; text-align:center; font-weight:bold; }
  table.grille th.moy { background:#dde7f1; }
  table.grille td.c { text-align:center; }
  table.grille td.l { text-align:left; }
  table.grille td.moy { font-weight:600; }
  table.grille td.gen { background:#f4f8fc; }
  table.grille td.sous { color:#b00020; }
  table.grille td.ok { color:#0a7d33; font-weight:bold; }
  table.grille td.ko { color:#b00020; font-weight:bold; }
  table.grille tbody tr:nth-child(even) td { background:#fafbfc; }
  .signature { margin-top:26px; display:flex; justify-content:flex-end; }
  .signature .box { text-align:center; font-size:11px; min-width:280px; }
  .signature .box .role { font-weight:bold; }
  .signature .box .space { height:48px; }
  .legend { font-size:9px; color:#555; margin-top:6px; font-style:italic; }
</style></head><body>

<div class="entete">
  <div class="col g">
    MINISTÈRE DE L'ENSEIGNEMENT SUPÉRIEUR<br/>
    <span class="inst">INSTITUT INTERNATIONAL POLYTECHNIQUE &amp; COMMERCE 2i</span><br/>
    Campus de ${esc(data.campus || 'Pointe-Noire')}
  </div>
  <div class="mid">
    <img src="/logo.png" alt="Logo 2i" onerror="this.style.display='none'" />
  </div>
  <div class="col d">
    RÉPUBLIQUE DU CONGO<br/>
    Unité – Travail – Progrès
  </div>
</div>

<div class="doc-title">BOTTIN DE RÉINSCRIPTION</div>
<div class="doc-sub">${esc(niveauLong)} · Année académique ${esc(anneeAff)}</div>

<div class="meta">
  <div>Niveau : <b>${esc(data.niveau)}</b> &nbsp;·&nbsp; Semestres : <b>${sems.map((s) => 'S' + s).join(', ')}</b></div>
  <div>Effectif : <b>${data.effectif_total}</b> &nbsp;·&nbsp; Éligibles (moy. ≥ ${fmt(data.seuil_eligibilite)}) : <b>${data.eligibles_total}</b></div>
</div>

${blocs}

<div class="legend">Éligible = moyenne générale des semestres disponibles ≥ ${fmt(data.seuil_eligibilite)}. Les moyennes &lt; 10 sont en rouge. « — » = semestre non disponible.</div>

<div class="signature">
  <div class="box">
    <div class="role">Le Chef de Service de la Scolarité</div>
    <div style="font-size:10px">Fait à ${esc(ville)}, le ${esc(fmtDate(opts.dateEmission))}</div>
    <div class="space"></div>
    <div>${esc(chef)}</div>
  </div>
</div>

</body></html>`;
}
